import React, { useEffect, useState } from 'react';
import {
  Button, Col, Divider, Form, Input, Modal, Row, Select, Spin, message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../api/client';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Individual {
  id: number;
  last_name: string;
  first_name: string;
  middle_name?: string;
  job_title?: string;
  phones: string[];
  emails: string[];
  roles: { role: string; counterparty_id: number | null }[];
}

export type IndividualDefaultRole = 'client' | 'employee' | 'counterparty_employee';

interface Props {
  open: boolean;
  onClose: () => void;
  defaultRole: IndividualDefaultRole;
  defaultCounterpartyId?: number;
  editId?: number;
  onCreated?: (individual: Individual) => void;
  onUpdated?: (individual: Individual) => void;
}

// ── ROLE_LABELS ───────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  employee: 'Сотрудник',
  client: 'Клиент',
  counterparty_employee: 'Сотрудник контрагента',
};

// ── ContactList ───────────────────────────────────────────────────────────────

const ContactList: React.FC<{
  items: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
}> = ({ items, onChange, placeholder }) => {
  const add = () => onChange([...items, '']);
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i: number, v: string) => {
    const next = [...items];
    next[i] = v;
    onChange(next);
  };
  const isLast = (i: number) => i === items.length - 1;
  return (
    <div>
      {items.map((val, i) => (
        <Row key={i} gutter={8} style={{ marginBottom: 8 }}>
          <Col flex="auto">
            <Input value={val} placeholder={placeholder} onChange={e => update(i, e.target.value)} />
          </Col>
          {!isLast(i) && (
            <Col>
              <Button icon={<DeleteOutlined />} danger size="small" onClick={() => remove(i)} />
            </Col>
          )}
          {isLast(i) && (
            <>
              {items.length > 1 && (
                <Col>
                  <Button icon={<DeleteOutlined />} danger size="small" onClick={() => remove(i)} />
                </Col>
              )}
              <Col>
                <Button icon={<PlusOutlined />} size="small" onClick={add} />
              </Col>
            </>
          )}
        </Row>
      ))}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const IndividualFormModal: React.FC<Props> = ({
  open, onClose, defaultRole, defaultCounterpartyId, editId, onCreated, onUpdated,
}) => {
  const [form] = Form.useForm();
  const [phones, setPhones] = useState<string[]>(['']);
  const [emails, setEmails] = useState<string[]>(['']);
  const [counterparties, setCounterparties] = useState<{ id: number; short_name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Load counterparties
  useEffect(() => {
    api.get('/counterparties', { params: { per_page: 100 } })
      .then(r => setCounterparties(r.data.items || []))
      .catch(() => {});
  }, []);

  // Reset form when modal opens; load data if editId
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setPhones(['']);
    setEmails(['']);

    if (editId) {
      setLoadingEdit(true);
      api.get(`/individuals/${editId}`)
        .then(r => {
          const ind = r.data;
          setPhones(ind.phones.length > 0 ? ind.phones : ['']);
          setEmails(ind.emails.length > 0 ? ind.emails : ['']);
          form.setFieldsValue({
            last_name: ind.last_name,
            first_name: ind.first_name,
            middle_name: ind.middle_name,
            gender: ind.gender,
            job_title: ind.job_title,
            notes: ind.notes,
            roles: ind.roles.map((r: any) => ({
              role: r.role,
              counterparty_id: r.counterparty_id,
            })),
          });
        })
        .catch(() => {})
        .finally(() => setLoadingEdit(false));
    } else {
      form.setFieldsValue({
        roles: [{
          role: defaultRole,
          counterparty_id: defaultCounterpartyId ?? null,
        }],
      });
    }
  }, [open, editId, defaultRole, defaultCounterpartyId, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        phones: phones.filter(p => p.trim()),
        emails: emails.filter(e => e.trim()),
        roles: values.roles || [],
      };
      const res = editId
        ? await api.put(`/individuals/${editId}`, payload)
        : await api.post('/individuals', payload);
      message.success(editId ? 'Физлицо обновлено' : 'Физлицо создано');
      if (editId) {
        onUpdated?.(res.data);
      } else {
        onCreated?.(res.data);
      }
      onClose();
    } catch (e: any) {
      if (e?.response?.data?.detail) message.error(e.response.data.detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editId ? 'Редактировать физлицо' : 'Новое физлицо'}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={editId ? 'Сохранить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={saving}
      width={600}
      destroyOnClose
    >
      <Spin spinning={loadingEdit}>
        <Form form={form} layout="vertical">
          {/* ФИО */}
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="last_name" label="Фамилия" rules={[{ required: true, message: 'Обязательное' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="first_name" label="Имя" rules={[{ required: true, message: 'Обязательное' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="middle_name" label="Отчество">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="gender" label="Пол">
                <Select allowClear options={[{ value: 'М', label: 'Мужской' }, { value: 'Ж', label: 'Женский' }]} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain style={{ fontSize: 13, color: '#888', margin: '8px 0' }}>
            Контакты
          </Divider>

          <Form.Item label="Телефон">
            <ContactList items={phones} onChange={setPhones} placeholder="+79001234567" />
          </Form.Item>

          <Form.Item label="Email">
            <ContactList items={emails} onChange={setEmails} placeholder="email@example.com" />
          </Form.Item>

          <Divider orientation="left" plain style={{ fontSize: 13, color: '#888', margin: '8px 0' }}>
            Работа
          </Divider>

          <Form.Item name="job_title" label="Должность">
            <Input placeholder="Менеджер по продажам" />
          </Form.Item>

          <Form.Item name="notes" label="Заметки">
            <Input.TextArea rows={2} />
          </Form.Item>

          {/* Роли */}
          <Form.Item label="Роли">
            <Form.List name="roles">
              {(fields, { add, remove }) => (
                <div>
                  {fields.map(({ key, name }) => (
                    <Row key={key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                      <Col span={8}>
                        <Form.Item name={[name, 'role']} noStyle>
                          <Select
                            style={{ width: '100%' }}
                            options={Object.entries(ROLE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, cur) =>
                            prev.roles?.[name]?.role !== cur.roles?.[name]?.role
                          }
                        >
                          {() => {
                            const role = form.getFieldValue(['roles', name, 'role']);
                            return role === 'counterparty_employee' ? (
                              <Form.Item name={[name, 'counterparty_id']} noStyle>
                                <Select
                                  style={{ width: '100%' }}
                                  placeholder="Контрагент"
                                  options={counterparties.map(c => ({ value: c.id, label: c.short_name }))}
                                />
                              </Form.Item>
                            ) : null;
                          }}
                        </Form.Item>
                      </Col>
                      <Col>
                        <Button size="small" danger onClick={() => remove(name)}>✕</Button>
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ role: 'client' })}>
                    Добавить роль
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default IndividualFormModal;
